import React, {
  useContext,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Button,
  Accordion,
  ScrollArea,
  useMantineColorScheme,
  useMantineTheme,
  Group,
} from "@mantine/core";
import { v4 as uuidv4 } from "uuid";
import WingCardComponent from "../../components/WingCardComponent";
import CanvasComponent from "../../components/CanvasComponent";
import { UserContext } from "@/contexts/UserContext/UserContext";
import { notifications } from "@mantine/notifications";
import { getAccordionStyles } from "@/helpers/getAccordionStyles";
import { getButtonStyles } from "@/helpers/getButtonStyles";
import { SailModelsType } from "@/types/apiTypes";
import { Validatable } from "@/pages/study/StudyStepper";
import styles from "../../css/study.module.css";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";

interface Wing {
  id: string;
  label: string;
  y: number;
  x: number;
  color: string;
  model: string;
}

type Props = {
  sailModels?: SailModelsType[];
};

const WindPropulsionPage = forwardRef<Validatable, Props>(
  ({ sailModels }, ref) => {
    const context = useContext(UserContext);
    const [wings, setWings] = useState<Wing[]>([]);
    const [wingModels, setWingModels] = useState<
      { value: string; label: string }[]
    >([]);

    const { colors, isDarkMode } = useAppColors();

    const accordionStyles = getAccordionStyles(isDarkMode);

    const shipWidth =
      context?.studyFormValues.vesselData.dtwin.vessel.beam_m || 50;
    const shipHeight =
      context?.studyFormValues.vesselData.dtwin.vessel.lpp_m || 300;

    const [canvasWidth] = useState<number>(200); // Width of the canvas
    const [canvasHeight] = useState<number>(400); // Height of the canvas

    const scaleX = canvasWidth / shipWidth;
    const scaleY = canvasHeight / shipHeight;

    const [activeAccordionItem, setActiveAccordionItem] = useState<
      string | null
    >(null);
    const theme = useMantineTheme();
    const colorScheme = useMantineColorScheme();
    const buttonStyles = getButtonStyles(isDarkMode);

    useEffect(() => {
      if (sailModels && sailModels.length > 0) {
        console.log("Fetched sail models:", sailModels);

        // Store the entire wind_propellers_lib in the context
        const wingModelsArray = sailModels[0].wind_propellers_lib;
        console.log(wingModelsArray);
        context?.setStudyFormValues((prevValues) => ({
          ...prevValues,
          windPropellersLib: wingModelsArray,
        }));
        console.log(context?.studyFormValues);
        // Transform for Select component
        const formattedModels = Object.keys(wingModelsArray).map((key) => ({
          value: key,
          label: key,
        }));
        setWingModels(formattedModels);
      }
    }, [sailModels]);
    useEffect(() => {
      console.log("Context has been updated:", context?.studyFormValues);
    }, [context?.studyFormValues]);

    // useEffect(() => {
    //   context?.setStudyFormValues((prevValues) => ({
    //     ...prevValues,
    //     wings: wings,
    //   }));
    // }, [wings]);

    useEffect(() => {
      if (
        context?.studyFormValues.wings &&
        context.studyFormValues.wings.length > 0
      ) {
        setWings(context.studyFormValues.wings);
      }
    }, []);

    const addWing = () => {
      const newWing: Wing = {
        id: uuidv4(),
        label: `Sail_${wings.length + 1}`,
        x: 0, // Bottom center
        y: 0, // Bottom of the ship
        color: getRandomColorExcludingBlue(),
        model: wingModels[0]?.value || "",
      };
      setWings([newWing, ...wings]);
      updateSailPositions([newWing, ...wings]);
    };

    const cloneWing = (id: string) => {
      const wingToClone = wings.find((wing) => wing.id === id);
      if (wingToClone) {
        const newColor = getRandomColorExcludingBlue();
        const clonedWing: Wing = {
          ...wingToClone,
          id: uuidv4(),
          label: `Sail_${wings.length + 1}`,
          color: newColor,
        };
        setWings([...wings, clonedWing]);
        updateSailPositions([...wings, clonedWing]);
      }
    };

    const removeWing = (id: string) => {
      const updatedWings = wings.filter((wing) => wing.id !== id);
      setWings(updatedWings);
      updateSailPositions(updatedWings);
    };

    const updateWing = (
      id: string,
      label: string,
      x: number,
      y: number,
      color: string,
      model: string,
    ) => {
      const updatedWings = wings.map((wing) =>
        wing.id === id ? { ...wing, label, x, y, color, model } : wing,
      );
      setWings(updatedWings);
      updateSailPositions(updatedWings);
    };

    const handleDragEnd = (id: string, x: number, y: number) => {
      const clampedX = Math.max(-shipWidth / 2, Math.min(shipWidth / 2, x));
      const clampedY = Math.max(0, Math.min(shipHeight, y));
      const updatedWings = wings.map((wing) =>
        wing.id === id ? { ...wing, x: clampedX, y: clampedY } : wing,
      );
      setWings(updatedWings);
      updateSailPositions(updatedWings);
    };

    const handleDragStart = (id: string) => {
      setActiveAccordionItem(id);
    };

    const updateSailPositions = (updatedWings: Wing[]) => {
      const sailPositions = updatedWings.reduce(
        (acc, wing) => {
          acc[wing.label] = {
            type: wing.model,
            position_m: { x: wing.y, y: wing.x, z: -27 }, // single, intentional swap
          };
          return acc;
        },
        {} as Record<string, any>,
      );

      const shipFrameWings = updatedWings.map((wing) => ({
        ...wing,
        x: wing.y,
        y: wing.x,
      }));

      context?.setStudyFormValues((prev) => ({
        ...prev,
        wings: shipFrameWings,
        sailPositions,
      }));
    };

    const validateSailConfig = (wings: Wing[]): boolean => {
      const labels = wings.map((wing) => wing.label);
      const sailModels = wings.map((wing) => wing.model);
      const positions = wings.map((wing) => ({
        x: wing.x,
        y: wing.y,
        label: wing.label,
      }));

      // 1. Check if there are any sails
      // if (labels.length === 0) {
      //   notifications.show({
      //     title: "Validation Error",
      //     message: "No sails added. Please add at least one sail.",
      //     color: "red",
      //     autoClose: 4000,
      //     position: "top-center",
      //   });
      //   return false;
      // }

      // 2. Check for unique labels
      const labelCounts = labels.reduce(
        (acc, label) => {
          acc[label] = (acc[label] || 0) + 1;
          return acc;
        },
        {} as { [key: string]: number },
      );

      const duplicates = Object.keys(labelCounts).filter(
        (label) => labelCounts[label] > 1,
      );
      if (duplicates.length > 0) {
        notifications.show({
          title: "Validation Error",
          message: `Duplicate sail labels found: ${duplicates.join(", ")}. Please use unique labels.`,
          color: "red",
          autoClose: 4000,
          position: "top-center",
        });
        return false;
      }

      // 3. Check for invalid label formats
      const validLabelPattern = /^\w+$/;
      const invalidLabels = labels.filter(
        (label) => !validLabelPattern.test(label),
      );
      if (invalidLabels.length > 0) {
        notifications.show({
          title: "Validation Error",
          message: `Invalid sail labels: ${invalidLabels.join(", ")}. Labels must contain only letters, numbers, and underscores.`,
          color: "red",
          autoClose: 4000,
          position: "top-center",
        });
        return false;
      }

      // 4. Check for missing sail models
      const noneSailModels = labels.filter(
        (label, index) => !sailModels[index],
      );
      if (noneSailModels.length > 0) {
        notifications.show({
          title: "Validation Error",
          message: `Sail models not selected for: ${noneSailModels.join(", ")}. Please select a model for each sail.`,
          color: "red",
          autoClose: 4000,
          position: "top-center",
        });
        return false;
      }

      // 5. Check for overlapping sails
      for (let i = 0; i < positions.length; i++) {
        for (let j = i + 1; j < positions.length; j++) {
          const pos1 = positions[i];
          const pos2 = positions[j];
          if (Math.abs(pos1.x - pos2.x) < 5 && Math.abs(pos1.y - pos2.y) < 5) {
            notifications.show({
              title: "Validation Error",
              message: `Sails '${pos1.label}' and '${pos2.label}' are too close to each other. Please adjust their positions.`,
              color: "red",
              autoClose: 4000,
              position: "top-center",
            });
            return false;
          }
        }
      }

      return true;
    };

    const getRandomColorExcludingBlue = (): string => {
      let hue = Math.floor(Math.random() * 360);

      // Avoid blue-like hues (180 - 240 degrees)
      while (hue >= 180 && hue <= 240) {
        hue = Math.floor(Math.random() * 360);
      }

      // Return a color with a random saturation (50-100%) and fixed lightness (50%)
      const saturation = Math.floor(Math.random() * 51) + 50; // Saturation between 50% and 100%
      return `hsl(${hue}, ${saturation}%, 50%)`;
    };

    useImperativeHandle(ref, () => ({
      validate: () => {
        return validateSailConfig(wings);
      },
    }));

    return (
      <div className={styles.stepContainer} style={{ width: "100%" }}>
        <div className={styles.content}>
          <ScrollArea
            style={{
              borderRadius: ".5rem",
              border: `1px solid ${colorScheme.colorScheme === "dark" ? theme.colors.dark[5] : "#e0e0e0"}`,
              backgroundColor: colors.gray,
              padding: "1rem",
              height: "40rem",
              width: "30rem",
            }}
          >
            <h2
              style={{
                color: colors.secondary,
              }}
            >
              Wind Propellers
            </h2>
            <div>
              <Accordion
                value={activeAccordionItem}
                onChange={setActiveAccordionItem}
                styles={accordionStyles}
                style={{ padding: "1rem" }}
              >
                {wings.map((wing) => (
                  <Accordion.Item key={wing.id} value={wing.id}>
                    <Accordion.Control>
                      <div
                        style={{
                          display: "inline-block",
                          width: "10px",
                          height: "10px",
                          backgroundColor: wing.color,
                          marginRight: "8px",
                          borderRadius: "50%",
                        }}
                      />
                      {wing.label}
                    </Accordion.Control>
                    <Accordion.Panel>
                      <WingCardComponent
                        id={wing.id}
                        label={wing.label}
                        x={wing.x}
                        y={wing.y}
                        color={wing.color}
                        model={wing.model}
                        models={wingModels}
                        onRemove={removeWing}
                        onUpdate={updateWing}
                        onClone={cloneWing}
                        scaleX={scaleX}
                        scaleY={scaleY}
                        minX={-shipWidth / 2}
                        maxX={shipWidth / 2}
                        minY={0}
                        maxY={shipHeight}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>
            <Group justify="center">
              <Button
                variant="inline"
                onClick={addWing}
                style={{
                  // marginTop: "2rem",
                  //backgroundColor: getColor("sailBtn"),
                  ...buttonStyles,
                }}
              >
                Add wind propellers
              </Button>
            </Group>
          </ScrollArea>
          <div
            style={{
              zIndex: 999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CanvasComponent
              wings={wings}
              width={canvasWidth}
              height={canvasHeight}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              scaleX={scaleX}
              scaleY={scaleY}
              shipWidth={shipWidth}
              shipHeight={shipHeight}
            />
          </div>
        </div>
      </div>
    );
  },
);

export default WindPropulsionPage;
